import { Injectable } from '@angular/core';
import { TranslateService } from '@ngx-translate/core';
import { NotificationService } from '../../_services/notification.service';
import { BackendMessageI18nService } from './backend-message-i18n.service';
import { BackendMessageSource } from './backend-message.catalog';

@Injectable({ providedIn: 'root' })
export class NotificationI18nService {
  constructor(
    private notification: NotificationService,
    private translate: TranslateService,
    private backendMessages: BackendMessageI18nService
  ) {}

  error(messageKey: string, durationOrParams?: number | Record<string, unknown>, duration = 5000): void {
    let params: Record<string, unknown> | undefined;
    let ms = duration;
    if (typeof durationOrParams === 'number') {
      ms = durationOrParams;
    } else if (durationOrParams) {
      params = durationOrParams;
    }
    this.notification.error(
      this.translate.instant('COMMON.NOTIFICATION.ERROR'),
      this.translate.instant(messageKey, params),
      ms
    );
  }

  success(messageKey: string, durationOrParams?: number | Record<string, unknown>, duration = 5000): void {
    let params: Record<string, unknown> | undefined;
    let ms = duration;
    if (typeof durationOrParams === 'number') {
      ms = durationOrParams;
    } else if (durationOrParams) {
      params = durationOrParams;
    }
    this.notification.success(
      this.translate.instant('COMMON.NOTIFICATION.SUCCESS'),
      this.translate.instant(messageKey, params),
      ms
    );
  }

  warning(messageKey: string, durationOrParams?: number | Record<string, unknown>, duration = 5000): void {
    let params: Record<string, unknown> | undefined;
    let ms = duration;
    if (typeof durationOrParams === 'number') {
      ms = durationOrParams;
    } else if (durationOrParams) {
      params = durationOrParams;
    }
    this.notification.warning(
      this.translate.instant('COMMON.NOTIFICATION.ATTENTION'),
      this.translate.instant(messageKey, params),
      ms
    );
  }

  info(messageKey: string, durationOrParams?: number | Record<string, unknown>, duration = 4000): void {
    let params: Record<string, unknown> | undefined;
    let ms = duration;
    if (typeof durationOrParams === 'number') {
      ms = durationOrParams;
    } else if (durationOrParams) {
      params = durationOrParams;
    }
    this.notification.info(
      this.translate.instant('COMMON.NOTIFICATION.INFO'),
      this.translate.instant(messageKey, params),
      ms
    );
  }

  /** Error de generación PDF: mensaje del servidor o clave i18n por defecto. */
  errorPdfGeneration(
    data: { message?: string; error?: string; responseError?: string } | null | undefined,
    duration = 5000
  ): void {
    const dynamic = data?.message ?? data?.error ?? data?.responseError;
    if (dynamic) {
      this.errorDynamic(dynamic, duration);
      return;
    }
    this.error('NOTIFICATION.MESSAGE.NO_HA_SIDO_POSIBLE_GENERAR_EL_DOCUMENTO_A_FIRMAR', duration);
  }

  private resolveMessage(message: BackendMessageSource): string {
    return this.backendMessages.resolve(message);
  }

  errorDynamic(message: BackendMessageSource, duration = 5000): void {
    this.notification.error(
      this.translate.instant('COMMON.NOTIFICATION.ERROR'),
      this.resolveMessage(message),
      duration
    );
  }

  /** Prefijo traducido + detalle dinámico (p. ej. errores de API). */
  errorWithDetail(messageKey: string, detail: string, duration = 5000): void {
    this.notification.error(
      this.translate.instant('COMMON.NOTIFICATION.ERROR'),
      `${this.translate.instant(messageKey)}${detail ?? ''}`,
      duration
    );
  }

  successDynamic(message: BackendMessageSource, duration = 5000): void {
    this.notification.success(
      this.translate.instant('COMMON.NOTIFICATION.SUCCESS'),
      this.resolveMessage(message),
      duration
    );
  }

  warningDynamic(message: BackendMessageSource, duration = 5000): void {
    this.notification.warning(
      this.translate.instant('COMMON.NOTIFICATION.ATTENTION'),
      this.resolveMessage(message),
      duration
    );
  }

  infoDynamic(message: BackendMessageSource, duration = 4000): void {
    this.notification.info(
      this.translate.instant('COMMON.NOTIFICATION.INFO'),
      this.resolveMessage(message),
      duration
    );
  }

  warningRaw(titleKey: string, messageKey: string, params?: Record<string, unknown>, duration = 5000): void {
    this.notification.warning(
      this.translate.instant(titleKey, params),
      this.translate.instant(messageKey, params),
      duration
    );
  }

  errorRedir(
    titleKey: string,
    messageKey: string,
    duration = 30000,
    messageUrlKey: string,
    url: string
  ): void {
    this.notification.errorRedir(
      this.translate.instant(titleKey),
      this.translate.instant(messageKey),
      duration,
      this.translate.instant(messageUrlKey),
      url
    );
  }
}
